import { useCallback } from "react";

import type { BillingStatus, TicketListAvailable_BillingPageQuery } from "@/lib/gql/__generated__/graphql";
import { CurrencyType } from "@/lib/gql/__generated__/graphql";

import type {
	BillingSettingDialogStateType,
	BillingSettingDialogType,
	OnUpdatePurchaseProBillingTicketType,
} from "@/components/features/settings/billing/model/use-case";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { CurrentTicketInfo } from "./components/current-ticket-info";
import { PurchaseDialogContent } from "./components/purchase-dialog-content";
import { useCurrentTicketState } from "./hooks/use-current-ticket-state";
import { PurchaseTicketDialog } from "../../../../_shared/dialog";
import { SettingsBillingSection } from "../../../../_shared/section";
import { useTicketList } from "../../../../_shared/ticket-list/ticket-list";

import type { TicketListItemType } from "../../../../_shared/ticket-list/ticket-list";

type ProTicketCurrentTicketSectionProps = {
	ticketProListAvailableSingleBilling: TicketListAvailable_BillingPageQuery["ticketProListAvailableSingleBilling"];
	billingStatus: BillingStatus;
	onPurchaseProBillingTicket: OnUpdatePurchaseProBillingTicketType;
	openBillingSettingDialog: BillingSettingDialogStateType;
	handleOpenChange: (type: BillingSettingDialogType) => (callBackFunc: NodeJS.Timeout) => (open: boolean) => void;
	activateBillingSettingDialog: (type: BillingSettingDialogType) => void;
	deactivateBillingSettingDialog: (type: BillingSettingDialogType) => void;
};

export const ProTicketCurrentTicketSection = ({
	ticketProListAvailableSingleBilling,
	billingStatus,
	onPurchaseProBillingTicket,
	openBillingSettingDialog,
	handleOpenChange: _handleOpenChange,
	activateBillingSettingDialog,
	deactivateBillingSettingDialog,
}: ProTicketCurrentTicketSectionProps) => {
	const { isStopRecurringProBilling, onUpdateIsStopRecurringProBilling, onResetIsStopRecurringProBilling } =
		useCurrentTicketState({ billingStatus });

	// チケットのrender hooks を初期化（チケットのリストコンポーネントと選択されているチケットIDを返す）
	const { selectedTicketID, ticketList_Component, handleChangeSelectedTicketID } = useTicketList({
		accentColor: "yellow",
		ticketList: ticketProListAvailableSingleBilling.tickets.map((ticket) => {
			return {
				id: ticket?.hotelProBillingTicketID ?? "",
				name: ticket?.name || "",
				validMonth: ticket?.validMonth || 0,
				price: ticket?.price || { amount: 0, currencyType: CurrencyType.Jpy },
			} satisfies TicketListItemType;
		}),
	});

	const handleOpenChange = useCallback(
		(open: boolean) =>
			_handleOpenChange("PRO")(
				setTimeout(() => {
					handleChangeSelectedTicketID("");
					onResetIsStopRecurringProBilling();
				}, 100),
			)(open),
		[_handleOpenChange, handleChangeSelectedTicketID, onResetIsStopRecurringProBilling],
	);
	const handleClick = useCallback(() => {
		activateBillingSettingDialog("PRO");
	}, [activateBillingSettingDialog]);

	const isValid = !!selectedTicketID;

	const handlePurchase = useCallback(async () => {
		if (!selectedTicketID) {
			return;
		}

		deactivateBillingSettingDialog("PRO");

		const result = await onPurchaseProBillingTicket({
			defaultProBillingTicketID: selectedTicketID,
			isStopRecurringProBilling,
		});

		if (result) {
			notifyUserEventInfo({
				title: "定額チケットを購入しました",
			});
		}
	}, [deactivateBillingSettingDialog, onPurchaseProBillingTicket, selectedTicketID, isStopRecurringProBilling]);

	return (
		<SettingsBillingSection title="現在適用中のチケット">
			<CurrentTicketInfo billingStatus={billingStatus} onAddTicketClick={handleClick} />

			<PurchaseTicketDialog
				title="予約手数料 | 定額チケットの購入"
				open={openBillingSettingDialog.PRO}
				handleOpenChange={handleOpenChange}
			>
				<PurchaseDialogContent
					ticketListComponent={ticketList_Component}
					isStopRecurringProBilling={isStopRecurringProBilling}
					onUpdateIsStopRecurringProBilling={onUpdateIsStopRecurringProBilling}
					isValid={isValid}
					onPurchase={handlePurchase}
				/>
			</PurchaseTicketDialog>
		</SettingsBillingSection>
	);
};

ProTicketCurrentTicketSection.displayName = "ProTicketCurrentTicketSection";
